/**
 * Plugin contract. A plugin is an ES module (loaded by URL) whose default
 * export is a PluginModule. Plugins run in the page with the same privileges as
 * the app itself: they are trusted code, like native OBS plugins. See
 * docs/architecture.md for the security model.
 */
import type { LevelReading } from '../audio/api';
import type {
  AudioFilterDefinition,
  FilterProbeResult,
  SourceTypeDefinition,
  TransitionDefinition,
  VideoFilterDefinition,
  VideoFrameSource,
} from '../core/definitions';
import type { Id, Project, SettingsBag } from '../core/model';
import type { Unsubscribe } from '../core/services';
import type { DockContribution, RuntimeState, StudioEvents } from '../engine/api';
import type { Commands, ProjectQueries } from '../state/api';
import type { ProgramSourceEvidence } from '../render/api';

export const PLUGIN_API_VERSION = 1;

export interface PluginManifest {
  /** Globally unique, reverse-DNS style: 'com.example.weather'. */
  id: string;
  name: string;
  version: string;
  /** PLUGIN_API_VERSION the plugin was written against. */
  apiVersion: number;
  description?: string;
}

export interface PluginModule {
  manifest: PluginManifest;
  activate(api: PluginApi): void | Promise<void>;
  /** Optional extra cleanup; everything registered through the api is removed automatically. */
  deactivate?(): void | Promise<void>;
}

export interface RemoteCommand {
  /** e.g. 'scene.select', 'recording.start', or a plugin-defined name. */
  type: string;
  payload?: unknown;
}

export interface PluginApi {
  readonly manifest: PluginManifest;

  // Custom sources, filters, transitions. Kinds must be prefixed with the plugin id.
  registerSource<S extends SettingsBag>(def: SourceTypeDefinition<S>): Unsubscribe;
  registerVideoFilter<S extends SettingsBag>(def: VideoFilterDefinition<S>): Unsubscribe;
  registerAudioFilter<S extends SettingsBag>(def: AudioFilterDefinition<S>): Unsubscribe;
  registerTransition<S extends SettingsBag>(def: TransitionDefinition<S>): Unsubscribe;

  // Overlays / UI: a dock panel the user can place in the layout.
  registerDock(dock: DockContribution): Unsubscribe;

  // Automation
  readonly commands: Commands;
  readonly queries: ProjectQueries;
  getProject(): Project;
  getRuntime(): RuntimeState;
  onProjectChange(listener: (project: Project) => void): Unsubscribe;
  onRuntimeChange(listener: (state: RuntimeState) => void): Unsubscribe;
  on<K extends keyof StudioEvents>(event: K, listener: (payload: StudioEvents[K]) => void): Unsubscribe;
  selectScene(sceneId: Id): void;
  transition(toSceneId?: Id): Promise<void>;
  startRecording(): Promise<void>;
  stopRecording(): Promise<void>;
  startBroadcast(): Promise<void>;
  stopBroadcast(): Promise<void>;
  getLevels(): Readonly<Record<string, LevelReading>>;
  registerHotkeyAction(action: { id: string; label: string; run(): void }): Unsubscribe;

  // Remote control: handle commands arriving from other tabs/devices and publish state.
  registerRemoteHandler(type: string, handler: (payload: unknown) => unknown | Promise<unknown>): Unsubscribe;

  // AI / analysis extensions: read composed frames and mixed audio without affecting output.
  /** The live program MediaStream (video + audio tracks). Do not stop its tracks. */
  getProgramStream(): MediaStream;
  /** Public geometry of sources consumed by the delivered program frame; never raw media. */
  getProgramSourceEvidence?(): ProgramSourceEvidence | null;
  /** The engine AudioContext, for analysers or custom nodes. */
  getAudioContext(): AudioContext;
  /** Validates a neutral public texture through the current compositor GPU without drawing it. */
  probeTexture?(source: VideoFrameSource): boolean;
  /**
   * Compiles a filter pass on the current compositor GPU and pre-warms it with
   * one offscreen 1x1 draw (never the program). 'unavailable' when no GPU is
   * usable right now (retry later), 'failed' when the pass cannot run here.
   */
  probeFilterFragment?(fragment: string, key: string, originSampler?: boolean): FilterProbeResult;

  // Per-plugin persisted data (stored in the project).
  getData(): SettingsBag;
  setData(data: SettingsBag): void;

  notify(level: 'info' | 'warn' | 'error', message: string): void;
}

export interface PluginHost {
  /** Import the module, validate its manifest and activate it. */
  load(url: string): Promise<PluginManifest>;
  unload(id: string): Promise<void>;
  list(): Array<{ manifest: PluginManifest; url: string; status: 'active' | 'error'; error: string | null }>;
  subscribe(listener: () => void): Unsubscribe;
  /** Dispatch a remote-control command to the built-in handlers or a plugin handler. */
  dispatchRemote(command: RemoteCommand): Promise<unknown>;
  destroy(): Promise<void>;
}
